import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { z } from 'zod'

import { convertAreaLabel } from '~/models/area'
import {
  validPeriodRefine,
  validPeriodRefineIssue,
  withinRunes,
} from '~/pages/AdminOcctoMaster/tabs/_common/schema'

/**
 * 所属 BG メンバー input schema。
 * bgMasterId は UUID 形式必須。
 * validFrom は google.type.Date 形式 {year, month, day}。
 *
 * bgLicenseType / bgAreaCode は選択した BgMaster の license_type /
 * area_code を denormalize した client-only field (bgName と同様、
 * `SubmitterBgMemberFields` の select onChange で setValue される)。
 * backend へは送信しない (Create/Edit モーダルの mapping で除外)。
 * 4710-plan-submitter-bg-validation 設計書 §4 Boundary/Error の
 * AC-005c defensive check (superRefine) がこの2 field を使って親の
 * licenseType/areaCode と突合する。既存 Edit member (今回のセッションで
 * 再選択されていない行) はこの2 field が undefined のままになりうるが、
 * 計画提出者の licenseType/areaCode は作成後 readonly (AC-006) なので
 * 不整合は生じ得ず、undefined は「検証対象外 (= 信頼する)」として扱う。
 */
const SubmitterBgMemberInputSchema = z.object({
  bgMasterId: z.string().min(1, 'BG を選択してください'),
  bgName: z.string().min(1, 'BG 名称は必須です'),
  bgLicenseType: z.nativeEnum(LicenseType).optional(),
  bgAreaCode: z.nativeEnum(AreaCode).optional(),
  validFrom: z.object({
    year: z.number().int().min(1900).max(2100),
    month: z.number().int().min(1).max(12),
    day: z.number().int().min(1).max(31),
  }),
  // クリア時は RHF field-array の制約で `null` が入る (PeriodFields の
  // clearAsNull JSDoc 参照)。`null` を許容し `undefined` へ正規化して、以降の
  // validPeriodRefine・送信は従来どおり `undefined = 無期限` で扱う。
  // `.optional()` を末尾に付けて object key の optional 性 (省略可) を保つ。
  validTo: z
    .object({
      year: z.number().int().min(1900).max(2100),
      month: z.number().int().min(1).max(12),
      day: z.number().int().min(1).max(31),
    })
    .nullable()
    .transform((v) => v ?? undefined)
    .optional(),
})

export type SubmitterBgMemberInputValues = z.infer<
  typeof SubmitterBgMemberInputSchema
>

/**
 * 日付 schema (google.type.Date 形式)
 */
const dateSchema = z.object({
  year: z.number().int().min(1900).max(2100),
  month: z.number().int().min(1).max(12),
  day: z.number().int().min(1).max(31),
})

function trailingDigitToAreaCode(digit: string): AreaCode | undefined {
  if (digit === '0') return AreaCode.OKINAWA
  const n = parseInt(digit, 10)
  if (n >= 1 && n <= 9) return n as AreaCode
  return undefined
}

const AREA_CODE_TRAILING_DIGIT: Record<AreaCode, string> = {
  [AreaCode.UNSPECIFIED]: '',
  [AreaCode.HOKKAIDO]: '1',
  [AreaCode.TOHOKU]: '2',
  [AreaCode.TOKYO]: '3',
  [AreaCode.CHUBU]: '4',
  [AreaCode.HOKURIKU]: '5',
  [AreaCode.KANSAI]: '6',
  [AreaCode.CHUGOKU]: '7',
  [AreaCode.SHIKOKU]: '8',
  [AreaCode.KYUSHU]: '9',
  [AreaCode.OKINAWA]: '0',
}

/**
 * 計画提出者マスタの Create / Edit 共通 Zod schema。
 *
 * submitterCode: 5 文字固定 (proto: string.len = 5)
 * submitterName: 必須
 * licenseType: UNSPECIFIED 以外
 * areaCode: UNSPECIFIED 以外
 * businessMasterId: UUID 必須
 * note: 任意、100 文字以内 (submitter.md AC-OMS-011)
 * validFrom: 必須
 * validTo: 任意
 * members: 0 件以上 (proto では 0 件許可)
 */
export const SubmitterMasterFormSchema = z
  .object({
    submitterCode: z
      .string()
      // 例は **G8882** (spec AC-OMS-001)。 形式エラー (AC-OMS-001a) の例が
      // G00A2 なのとは別物で、 桁数エラーはこちらが正。 PR #6543 で形式エラーを
      // spec 準拠にした際、 桁数エラー側の例まで巻き込んで書き換わっていた。
      .length(5, '計画提出者コードは5文字で入力してください (例: G8882)')
      // 計画提出者コードは BG コードと別体系: 1 文字目=区分 G/D、2 文字目=数字、
      // 3〜4 文字目=英数字 (I・O 除外)、5 文字目=エリアコード数字。backend
      // valueobject.ParseSubmitterCode と同一 (spec AC-OMS-001a、issue #6279)。
      .regex(
        /^[GD]\d[A-HJ-NP-Z0-9]{2}\d$/,
        '「G・D + 数字 + 英数字2文字 + エリアコード」の5文字で入力してください。3〜4文字目に I・O は使えません (例: G00A2)',
      ),
    submitterName: z
      .string()
      .min(1, '計画提出者名称を入力してください')
      .refine(
        withinRunes(100),
        '計画提出者名称は 100文字以内で入力してください',
      ),
    licenseType: z
      .nativeEnum(LicenseType)
      .refine(
        (v) => v !== LicenseType.UNSPECIFIED,
        'BG 区分を選択してください',
      ),
    areaCode: z
      .nativeEnum(AreaCode)
      .refine((v) => v !== AreaCode.UNSPECIFIED, 'エリアを選択してください'),
    businessMasterId: z.string().min(1, '事業者を選択してください'),
    note: z.string().max(100, '備考は100文字以内で入力してください').optional(),
    validFrom: dateSchema,
    validTo: dateSchema.optional(),
    members: z.array(SubmitterBgMemberInputSchema),
  })
  .superRefine((data, ctx) => {
    // areaCode は上の .refine で UNSPECIFIED が除外済み (= 型 narrowing 後)。
    if (data.submitterCode.length !== 5) {
      return
    }
    const expected = trailingDigitToAreaCode(data.submitterCode[4])
    if (expected !== undefined && expected !== data.areaCode) {
      const label = convertAreaLabel(data.areaCode)
      const digit = AREA_CODE_TRAILING_DIGIT[data.areaCode]
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${label}では、計画提出者コードの末尾を「${digit}」にしてください`,
        path: ['submitterCode'],
      })
    }

    // AC-OMS-005a: 所属 BG の重複検出。2 件目以降の重複行に field-level
    // issue を付与する (BgMaster schema の addOwnerMemberIssues と異なり
    // spec 文言が行単位表示を要求するため、ここでは members[i] の
    // bgMasterId path に直接 addIssue する)。
    const seenBgMasterIds = new Set<string>()
    for (const [index, member] of data.members.entries()) {
      if (member.bgMasterId === '') continue
      if (seenBgMasterIds.has(member.bgMasterId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '所属 BG から重複している BG を削除してください',
          path: ['members', index, 'bgMasterId'],
        })
        continue
      }
      seenBgMasterIds.add(member.bgMasterId)
    }

    // AC-OMS-005c: 所属 BG の BG区分・エリア不一致検出 (defensive)。
    // bgLicenseType / bgAreaCode が未設定 (= 再選択されていない既存 Edit
    // member) の行は検証対象外とする (SubmitterBgMemberInputSchema の
    // JSDoc 参照)。
    for (const [index, member] of data.members.entries()) {
      if (member.bgMasterId === '') continue
      const licenseMismatch =
        member.bgLicenseType !== undefined &&
        member.bgLicenseType !== data.licenseType
      const areaMismatch =
        member.bgAreaCode !== undefined && member.bgAreaCode !== data.areaCode
      if (licenseMismatch || areaMismatch) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '所属 BG には、BG 区分・エリアが同じ BG を選択してください',
          path: ['members', index, 'bgMasterId'],
        })
      }
    }
  })
  // 適用終了日 ≥ 適用開始日 (同日許容、AC-03)。7 タブ共通 refine。
  .refine(validPeriodRefine, validPeriodRefineIssue)

export type SubmitterMasterFormValues = z.infer<
  typeof SubmitterMasterFormSchema
>
// Zod v4 では .refine の narrowing で z.input ≠ z.output になる (= UNSPECIFIED を input では
// 許容するが output では除外)。 useForm の defaultValues / Control / form fields は narrowing
// 前の Input 型を受ける必要があり、 submit handler だけが output (= narrow) を受け取る。
export type SubmitterMasterFormInput = z.input<typeof SubmitterMasterFormSchema>
